import React, { useState, useEffect } from 'react';
import { 
  Boxes, Plus, X, Search, Edit2, 
  Archive, ChevronDown, ChevronRight, RefreshCcw, AlertTriangle, CheckCircle2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import API, { socket } from '../../services/api';

const ListePsf = () => {
  const navigate = useNavigate();
  
  // --- États ---
  const [produitsSemiFinis, setProduitsSemiFinis] = useState([]);
  const [unites, setUnites] = useState([]); // 🌟 État pour stocker les unités dynamiques
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('catalogue');
  const [expandedId, setExpandedId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // État pour la notification (Toast intégré en haut)
  const [notification, setNotification] = useState({
    isOpen: false,
    type: 'success', // 'success', 'error', 'confirm'
    message: '',
    onConfirm: null
  });

  // État du formulaire
  const [formData, setFormData] = useState({
    id: '',
    nom: '', 
    unite_mesure: '', 
    cmp: 0, 
    stock_alerte: 10
  });

  // --- Logique ---
  const genererIdPsf = () => `PSF-${Date.now().toString().slice(-6)}`;

  useEffect(() => {
    fetchInitialData();

    const handleGlobalUpdate = (event) => {
        const { table } = event.detail;
        if (['fiches_techniques', 'products', 'psf', 'unites', 'all'].includes(table)) {
            fetchInitialData();
        }
    };

    window.addEventListener('ERP_DATA_CHANGED', handleGlobalUpdate);
    return () => window.removeEventListener('ERP_DATA_CHANGED', handleGlobalUpdate);
  }, []);

  // --- BLOC SOCKET ---
  useEffect(() => {
    if (!socket) return;

    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const companyId = userData.company_id || userData.companyId;
    
    if (companyId) {
      socket.emit('join_company', companyId.toString());
    }

    const handleSocketRefresh = (data) => {
      console.log("📡 Signal reçu dans PSF:", data);
      if (!data || data.module === 'PSF' || data.module === 'STOCKS' || data.type === 'RESERVE_RELEASED' || data.module === 'UNITES') {
        console.log("🔄 Action : Refreshing PSF Data...");
        fetchInitialData();
      }
    };

    socket.on('REFRESH_UI', handleSocketRefresh);
    socket.on('STOCK_UPDATE', handleSocketRefresh);
    
    return () => {
      socket.off('REFRESH_UI', handleSocketRefresh);
      socket.off('STOCK_UPDATE', handleSocketRefresh);
    };
  }, []);

  // Auto-fermeture automatique des notifications
  useEffect(() => {
    if (notification.isOpen && notification.type !== 'confirm') {
      const timer = setTimeout(() => {
        setNotification(prev => ({ ...prev, isOpen: false }));
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [notification.isOpen, notification.type]);

  // ✅ CHARGEMENT INITIAL DES PSF ET DES UNITÉS DE MESURE
  const fetchInitialData = async () => {
    try {
      const [psfRes, uRes] = await Promise.all([
        API.get('/semi-finished-products').catch(() => ({ data: [] })),
        API.get('/unites').catch(() => ({ data: [] }))
      ]);

      setProduitsSemiFinis(Array.isArray(psfRes.data) ? psfRes.data : []);

      // Extraction robuste des unités (gère différents formats de réponse API)
      const rawUnites = Array.isArray(uRes.data) ? uRes.data : (uRes.data?.unites || uRes.data?.data || []);
      setUnites(Array.isArray(rawUnites) ? rawUnites.filter(u => u.is_active !== 0) : []);

    } catch (err) { 
      showToast('Impossible de charger les données.', 'error');
    }
  };

  const showToast = (message, type = 'success') => {
    setNotification({
      isOpen: true,
      type,
      message,
      onConfirm: null
    });
  };

  const showConfirm = (message, onConfirmAction) => {
    setNotification({
      isOpen: true,
      type: 'confirm',
      message,
      onConfirm: onConfirmAction
    });
  };

  const handleOpenCreate = () => {
    setIsEditing(false);
    setFormData({ id: genererIdPsf(), nom: '', unite_mesure: '', cmp: 0, stock_alerte: 10 });
    setIsFormOpen(true);
  };

  const handleEditClick = (e, psf) => {
    e.stopPropagation();
    setIsEditing(true);
    setFormData({
      id: psf.localId || psf.id,
      nom: psf.nom,
      unite_mesure: psf.unite_mesure,
      cmp: psf.cmp,
      stock_alerte: psf.stock_alerte || 0
    });
    setIsFormOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const payload = {
        ...formData,
        company_id: userData.company_id || "COMP-01",
        userId: userData.id || "USR-01", 
        userName: userData.username || "Utilisateur"
    };

    try {
        if (isEditing) {
            await API.put(`/semi-finished-products/${formData.id}`, payload);
            showToast("Produit mis à jour avec succès !", "success");
            setIsEditing(false);
        } else {
            await API.post('/semi-finished-products', payload);
            showToast("Produit créé avec succès !", "success");
        }
        setFormData({ id: genererIdPsf(), nom: '', unite_mesure: '', cmp: 0, stock_alerte: 10 });
        setIsFormOpen(false); 
        fetchInitialData();
    } catch (err) {
        showToast(err.response?.data?.error || "Erreur de sauvegarde.", "error");
    } finally {
        setIsLoading(false);
    }
  };

  const toggleStatus = (e, psf) => {
    e.stopPropagation();
    const isArchiving = psf.is_active === 1;
    showConfirm(
      `Voulez-vous vraiment ${isArchiving ? 'archiver' : 'restaurer'} "${psf.nom}" ?`,
      () => performStatusToggle(psf, isArchiving)
    );
  };

  const performStatusToggle = async (psf, isArchiving) => {
    try {
        await API.patch(`/semi-finished-products/${psf.id}`, { is_active: isArchiving ? 0 : 1 });
        setNotification({ isOpen: false });
        showToast(`Produit ${isArchiving ? 'archivé' : 'restauré'} !`, "success");
        fetchInitialData();
    } catch (err) {
        showToast("Impossible de modifier le statut.", "error");
    }
  };

  const primaryColor = '#991b1b'; 
  const borderColor = '#e2e8f0';

  const s = {
    layout: { display: 'flex', height: '100vh', overflow: 'hidden', background: '#f1f5f9' },
    mainArea: { flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', position: 'relative' },
    topSection: { flexShrink: 0, zIndex: 10, background: 'white', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' },
    scrollableContent: { flex: 1, overflowY: 'auto', padding: '20px 40px' },
    formPanel: {
      maxHeight: isFormOpen ? '450px' : '0',
      overflow: 'hidden',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      borderBottom: isFormOpen ? `3px solid ${primaryColor}` : '0px solid transparent',
      padding: isFormOpen ? '25px 40px' : '0 40px'
    },
    input: { padding: '12px', borderRadius: '8px', border: `1px solid ${borderColor}`, fontSize: '14px', width: '100%', outlineColor: primaryColor },
    tab: (active) => ({
      padding: '16px 24px', cursor: 'pointer',
      borderBottom: active ? `3px solid ${primaryColor}` : '3px solid transparent',
      color: active ? primaryColor : '#64748b', fontWeight: active ? '800' : '600',
      display: 'flex', alignItems: 'center', gap: '10px', transition: '0.2s'
    }),
    actionBtn: { border: 'none', background: '#f8fafc', padding: '8px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: '0.2s' },
    toastContainer: {
      position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)',
      zIndex: 1000, display: 'flex', alignItems: 'center',
      background: 'white', padding: '12px 20px', borderRadius: '15px',
      boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
      border: `1px solid ${borderColor}`,
      minWidth: '400px',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    },
    confirmBtn: { background: primaryColor, color: 'white', border: 'none', padding: '6px 15px', borderRadius: '6px', fontWeight: '800', cursor: 'pointer', fontSize: '11px' },
    cancelBtn: { background: '#f1f5f9', color: '#64748b', border: 'none', padding: '6px 15px', borderRadius: '6px', fontWeight: '800', cursor: 'pointer', fontSize: '11px' }
  };

  const NotificationToast = () => {
    if (!notification.isOpen) return null;
    
    const isConfirm = notification.type === 'confirm';
    const isError = notification.type === 'error';
    const color = isError ? '#ef4444' : (isConfirm ? '#f59e0b' : '#10b981');

    return (
      <div style={{ ...s.toastContainer, borderLeft: `8px solid ${color}` }}>
        <div style={{ 
            display: 'flex', justifyContent: 'center', alignItems: 'center', 
            width: '35px', height: '35px', borderRadius: '50%', 
            background: `${color}15`, marginRight: '15px', flexShrink: 0
        }}>
          {isError ? <X size={20} color={color} /> : 
           isConfirm ? <AlertTriangle size={20} color={color} /> : 
           <CheckCircle2 size={20} color={color} />}
        </div>
        
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontWeight: '700', color: '#1e293b', fontSize: '14px' }}>
            {notification.message}
          </p>
          
          {isConfirm && (
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button onClick={notification.onConfirm} style={s.confirmBtn}>OUI, CONTINUER</button>
              <button onClick={() => setNotification({isOpen: false})} style={s.cancelBtn}>ANNULER</button>
            </div>
          )}
        </div>

        {!isConfirm && (
          <button 
            onClick={() => setNotification(prev => ({ ...prev, isOpen: false }))}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', marginLeft: '10px' }}
          >
            <X size={20} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div style={s.layout}>
      <Sidebar />
      
      <main style={s.mainArea}>
        <NotificationToast />

        <div style={s.topSection}>
            <header style={{ padding: '20px 40px', borderBottom: `1px solid ${borderColor}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '900' }}>
                <span style={{color: primaryColor}}>PRODUITS</span> SEMI-FINIS
            </h1>
            <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ position: 'relative' }}>
                <Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} size={18} />
                <input 
                    type="text" placeholder="Rechercher..." 
                    style={{ padding: '11px 40px', borderRadius: '10px', border: `1px solid ${borderColor}`, width: '250px' }} 
                    value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} 
                />
                </div>
                <button 
                  onClick={isFormOpen ? () => setIsFormOpen(false) : handleOpenCreate} 
                  style={{ background: isFormOpen ? '#fee2e2' : primaryColor, color: isFormOpen ? '#ef4444' : 'white', padding: '10px 20px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                {isFormOpen ? <X size={18} /> : <Plus size={18} />}
                {isFormOpen ? 'FERMER' : 'NOUVEAU PSF'}
                </button>
            </div>
            </header>

            <div style={s.formPanel}>
                <div style={{ maxWidth: '1200px' }}>
                    <form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: '15px', alignItems: 'end' }}>
                    <div>
                        <label style={{ fontSize: '11px', fontWeight: '800', color: '#64748b' }}>DÉSIGNATION</label>
                        <input required style={s.input} placeholder="Ex: PÂTE FEUILLETÉE" value={formData.nom} onChange={e => setFormData({...formData, nom: e.target.value})} />
                    </div>
                    <div>
                        <label style={{ fontSize: '11px', fontWeight: '800', color: '#64748b' }}>UNITÉ DE MESURE</label>
                        {/* 🌟 SELECT DYNAMIQUE DES UNITÉS */}
                        <select 
                          required 
                          style={{...s.input, background: 'white'}} 
                          value={formData.unite_mesure} 
                          onChange={e => setFormData({...formData, unite_mesure: e.target.value})}
                        >
                          <option value="">Choisir une unité...</option>
                          {unites.map(u => {
                            const libelleUnite = u.libelle || u.LIBELLE || u.nom || `Unité ${u.id}`;
                            const codeUnite = u.code || u.CODE ? ` (${u.code || u.CODE})` : '';
                            const valeurOption = u.libelle || u.code || u.id; 
                            return (
                              <option key={u.id || u.ID} value={valeurOption}>
                                {libelleUnite}{codeUnite}
                              </option>
                            );
                          })}
                        </select>
                    </div>
                    <div>
                        <label style={{ fontSize: '11px', fontWeight: '800', color: '#64748b' }}>COÛT (CMP)</label>
                        <input type="number" step="0.01" style={s.input} value={formData.cmp} onChange={e => setFormData({...formData, cmp: e.target.value})} />
                    </div>
                    <div>
                        <label style={{ fontSize: '11px', fontWeight: '800', color: '#64748b' }}>SEUIL ALERTE</label>
                        <input type="number" style={s.input} value={formData.stock_alerte} onChange={e => setFormData({...formData, stock_alerte: e.target.value})} />
                    </div>
                    <button disabled={isLoading} type="submit" style={{ background: primaryColor, color: 'white', border: 'none', padding: '13px 25px', borderRadius: '8px', fontWeight: '900', cursor: 'pointer', opacity: isLoading ? 0.7 : 1 }}>
                        {isLoading ? '...' : (isEditing ? 'METTRE À JOUR' : 'VALIDER')}
                    </button>
                    </form>
                </div>
            </div>
        </div>

        <div style={s.scrollableContent}>
          <div style={{ display: 'flex', background: 'white', borderRadius: '12px 12px 0 0', border: `1px solid ${borderColor}`, borderBottom: 'none', width: 'fit-content' }}>
            <div style={s.tab(activeTab === 'catalogue')} onClick={() => setActiveTab('catalogue')}>
              <Boxes size={18} /> Actifs ({produitsSemiFinis.filter(a => a.is_active === 1).length})
            </div>
            <div style={s.tab(activeTab === 'archives')} onClick={() => setActiveTab('archives')}>
              <Archive size={18} /> Archives ({produitsSemiFinis.filter(a => a.is_active === 0).length})
            </div>
          </div>

          <div style={{ background: 'white', borderRadius: '0 12px 12px 12px', border: `1px solid ${borderColor}`, overflow: 'hidden', marginBottom: '40px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', textAlign: 'left', borderBottom: `1px solid ${borderColor}` }}>
                  <th style={{ width: '50px', padding: '15px' }}></th>
                  <th style={{ padding: '15px', color: '#475569', fontSize: '11px', fontWeight: '800' }}>IDENTIFIANT</th>
                  <th style={{ padding: '15px', color: '#475569', fontSize: '11px', fontWeight: '800' }}>DÉSIGNATION</th>
                  <th style={{ padding: '15px', color: '#475569', fontSize: '11px', fontWeight: '800' }}>STOCK PHYSIQUE</th>
                  <th style={{ padding: '15px', color: '#f59e0b', fontSize: '11px', fontWeight: '800' }}>RÉSERVÉ</th>
                  <th style={{ padding: '15px', color: '#10b981', fontSize: '11px', fontWeight: '800' }}>DISPONIBLE</th>
                  <th style={{ padding: '15px', color: '#475569', fontSize: '11px', fontWeight: '800' }}>CMP</th>
                  <th style={{ padding: '15px', color: '#475569', fontSize: '11px', fontWeight: '800' }}>VALEUR TOTAL</th>
                  <th style={{ padding: '15px', textAlign: 'right', color: '#475569', fontSize: '11px', fontWeight: '800' }}>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {produitsSemiFinis
                  .filter(psf => (activeTab === 'catalogue' ? psf.is_active === 1 : psf.is_active === 0))
                  .filter(psf => psf.nom.toLowerCase().includes(searchTerm.toLowerCase()))
                  .map(psf => {
                    const phys = Number(psf.stock_actuel || 0);
                    const resv = Number(psf.stock_reserve || 0);
                    const dispo = phys - resv;
                    const isLowStock = dispo <= (psf.stock_alerte || 0);

                    return (
                      <tr key={psf.id} style={{ borderBottom: `1px solid ${borderColor}`, background: expandedId === psf.id ? '#fef2f2' : 'transparent', cursor: 'pointer' }} onClick={() => setExpandedId(expandedId === psf.id ? null : psf.id)}>
                        <td style={{ padding: '15px', textAlign: 'center' }}>
                          {expandedId === psf.id ? <ChevronDown size={18} color={primaryColor} /> : <ChevronRight size={18} color="#cbd5e1" />}
                        </td>
                        <td style={{ padding: '15px' }}>
                          <span style={{ fontWeight: '700', color: primaryColor, fontFamily: 'monospace' }}>{psf.id}</span>
                        </td>
                        <td style={{ padding: '15px', fontWeight: '800' }}>{psf.nom}</td>
                        <td style={{ padding: '15px' }}>
                          <div style={{ color: '#0f172a', fontWeight: '700' }}>
                            {phys.toFixed(2)} <span style={{fontSize: '10px'}}>{psf.unite_mesure}</span>
                          </div>
                        </td>
                        <td style={{ padding: '15px' }}>
                          <div style={{ color: '#f59e0b', fontWeight: '800' }}>
                            {resv > 0 ? `-${resv.toFixed(2)}` : '0.00'}
                          </div>
                        </td>
                        <td style={{ padding: '15px' }}>
                          <div style={{ color: isLowStock && psf.is_active ? '#ef4444' : '#10b981', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '5px' }}>
                            {dispo.toFixed(2)} {isLowStock && psf.is_active && <AlertTriangle size={14} />}
                          </div>
                        </td>
                        <td style={{ padding: '15px', fontWeight: '700', color: '#475569' }}>
                          {parseFloat(psf.cmp || 0).toLocaleString()} 
                        </td>
                        <td style={{ padding: '15px', fontWeight: '800' }}>
                          {(dispo * (psf.cmp || 0)).toLocaleString()} 
                        </td>
                        <td style={{ padding: '15px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            {psf.is_active === 1 ? (
                              <>
                                <button style={s.actionBtn} onClick={(e) => handleEditClick(e, psf)}><Edit2 size={16} color="#3b82f6" /></button>
                                <button style={s.actionBtn} onClick={(e) => toggleStatus(e, psf)}><Archive size={16} color="#64748b" /></button>
                              </>
                            ) : (
                              <button style={{...s.actionBtn, color: '#10b981', gap: '5px', padding: '8px 15px'}} onClick={(e) => toggleStatus(e, psf)}>
                                <RefreshCcw size={16} /> Restaurer
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ListePsf;